import i18n from "@/i18n/i18n";
// 应用常量定义

// API 相关常量
export const API_ENDPOINTS = {
  // 品牌相关
  BRAND_STATS: "/brand/stats",
  BRAND_LIST: "/brand/list",
  BRAND_DETAIL: "/brand/:id",
  BRAND_ADD: "/brand/add",
  BRAND_UPDATE: "/brand/:id",
  BRAND_DELETE: "/brand/:id",
  BRAND_TREND: "/brand/:id/trend",
  BRAND_COMPARISON: "/brand/comparison",
  BRAND_MENTIONS: "/brand/mentions",
  BRAND_ALERTS: "/brand/alerts",
  BRAND_BATCH: "/brand/batch",
  BRAND_EXPORT: "/brand/export",

  // 市场分析相关
  MARKET_ANALYSIS: "/market/analysis",
  MARKET_TREND: "/market/trend",
  MARKET_INSIGHTS: "/market/insights",
  MARKET_COMPARISON: "/market/comparison",

  // 竞争情报相关
  COMPETITOR_LIST: "/competitor/list",
  COMPETITOR_DETAIL: "/competitor/:id",
  COMPETITOR_INTEL: "/competitor/intel",
  COMPETITOR_ANALYSIS: "/competitor/analysis",
  COMPETITOR_ALERTS: "/competitor/alerts",

  // 报告相关
  REPORTS_LIST: "/reports/list",
  REPORTS_CREATE: "/reports/create",
  REPORTS_DETAIL: "/reports/:id",
  REPORTS_DOWNLOAD: "/reports/:id/download",
  REPORTS_TEMPLATES: "/reports/templates",
} as const;

// 平台类型
export const PLATFORMS = {
  WEIBO: "weibo",
  WEIXIN: "weixin",
  DOUYIN: "douyin",
  XIAOHONGSHU: "xiaohongshu",
  BILIBILI: "bilibili",
  ZHIHU: "zhihu",
} as const;

// 平台显示名称
export const PLATFORM_NAMES = {
  [PLATFORMS.WEIBO]: "微博",
  [PLATFORMS.WEIXIN]: "微信",
  [PLATFORMS.DOUYIN]: i18n.t("home_components.bubble_douyin"),
  [PLATFORMS.XIAOHONGSHU]: "小红书",
  [PLATFORMS.BILIBILI]: "B站",
  [PLATFORMS.ZHIHU]: i18n.t("home_components.bubble_zhihu"),
} as const;

// 情感类型
export const SENTIMENT_TYPES = {
  POSITIVE: "positive",
  NEGATIVE: "negative",
  NEUTRAL: "neutral",
} as const;

// 情感显示名称
export const SENTIMENT_NAMES = {
  [SENTIMENT_TYPES.POSITIVE]: i18n.t("common.modals.reputation_positive_tag"),
  [SENTIMENT_TYPES.NEGATIVE]: i18n.t("common.modals.reputation_negative_tag"),
  [SENTIMENT_TYPES.NEUTRAL]: "中性",
} as const;

// 情感颜色
export const SENTIMENT_COLORS = {
  [SENTIMENT_TYPES.POSITIVE]: "#52c41a",
  [SENTIMENT_TYPES.NEGATIVE]: "#ff4d4f",
  [SENTIMENT_TYPES.NEUTRAL]: "#faad14",
} as const;

// 状态类型
export const STATUS_TYPES = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  PENDING: "pending",
  DELETED: "deleted",
} as const;

// 状态显示名称
export const STATUS_NAMES = {
  [STATUS_TYPES.ACTIVE]: "活跃",
  [STATUS_TYPES.INACTIVE]: "暂停",
  [STATUS_TYPES.PENDING]: "待处理",
  [STATUS_TYPES.DELETED]: "已删除",
} as const;

// 状态颜色
export const STATUS_COLORS = {
  [STATUS_TYPES.ACTIVE]: "green",
  [STATUS_TYPES.INACTIVE]: "red",
  [STATUS_TYPES.PENDING]: "orange",
  [STATUS_TYPES.DELETED]: "default",
} as const;

// 报告类型
export const REPORT_TYPES = {
  BRAND_MONITOR: "brand_monitor",
  MARKET_ANALYSIS: "market_analysis",
  COMPETITOR_INTEL: "competitor_intel",
  CUSTOM: "custom",
} as const;

// 报告格式
export const REPORT_FORMATS = {
  PDF: "pdf",
  EXCEL: "excel",
  CSV: "csv",
  HTML: "html",
} as const;

// 分页配置
export const PAGINATION_CONFIG = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: ["10", "20", "50", "100"],
  SHOW_SIZE_CHANGER: true,
  SHOW_QUICK_JUMPER: true,
  SHOW_TOTAL: (total: number) => `共 ${total} 条记录`,
} as const;

// 时间格式
export const DATE_FORMATS = {
  DATE: "YYYY-MM-DD",
  DATETIME: "YYYY-MM-DD HH:mm:ss",
  TIME: "HH:mm:ss",
  MONTH: "YYYY-MM",
  YEAR: "YYYY",
} as const;

// 图表颜色配置
export const CHART_COLORS = {
  PRIMARY: "#1890ff",
  SUCCESS: "#52c41a",
  WARNING: "#faad14",
  ERROR: "#ff4d4f",
  INFO: "#13c2c2",
  PURPLE: "#722ed1",
  ORANGE: "#fa8c16",
  CYAN: "#36cfc9",
  LIME: "#a0d911",
  GOLD: "#fadb14",
} as const;

// 默认图表颜色数组
export const DEFAULT_CHART_COLORS = [
  CHART_COLORS.PRIMARY,
  CHART_COLORS.SUCCESS,
  CHART_COLORS.WARNING,
  CHART_COLORS.ERROR,
  CHART_COLORS.INFO,
  CHART_COLORS.PURPLE,
  CHART_COLORS.ORANGE,
  CHART_COLORS.CYAN,
  CHART_COLORS.LIME,
  CHART_COLORS.GOLD,
] as const;

// 文件上传配置
export const UPLOAD_CONFIG = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_TYPES: [
    "image/jpeg",
    "image/png",
    "image/gif",
    "application/pdf",
    "application/vnd.ms-excel",
  ],
  MAX_FILES: 5,
} as const;

// 缓存配置
export const CACHE_CONFIG = {
  DEFAULT_STALE_TIME: 5 * 60 * 1000, // 5分钟
  DEFAULT_GC_TIME: 30 * 60 * 1000, // 30分钟
  RETRY_COUNT: 3,
  RETRY_DELAY: 1000,
} as const;

// 数据源ID配置
export const SOURCE_ID = {
  DOMESTIC: 2, // 国内版
  OVERSEAS: 3, // 国外版
} as const;

/**
 * 获取当前数据源ID
 * 默认返回海外版（3），可通过环境变量或配置修改
 */
export const getSourceId = (): number => {
  // 可以通过环境变量或localStorage来配置
  const storedSourceId = localStorage.getItem("sourceId");
  if (storedSourceId) {
    const sourceId = parseInt(storedSourceId, 10);
    if (sourceId === SOURCE_ID.DOMESTIC || sourceId === SOURCE_ID.OVERSEAS) {
      return sourceId;
    }
  }
  // 默认返回海外版
  return SOURCE_ID.OVERSEAS;
};
